// filepath: apps/web/src/features/admin/recordPermissions/RecordPermissionConflictDialog.tsx
//
// FR-UI-ADM-2 AC-4 — modal dialog that surfaces the FR-UI-CORE-6
// refetch / merge / discard UX when the server returns HTTP 409
// ROLE_VERSION_MISMATCH (or the entity-field-owner equivalent).
//
// Non-skippable: the only way out of this dialog is one of the
// three action buttons (or the explicit Cancel button). There is
// no auto-retry on the mutation side — INV-7.
//
// Mirrors apps/web/src/features/admin/MatrixConflictDialog.tsx
// (FR-UI-ADM-1's sibling). Re-uses the same UX contract so the
// reviewer/admin muscle memory transfers.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { RecordPermissionConflict } from "./types";

interface RecordPermissionConflictDialogProps {
  open: boolean;
  conflict: RecordPermissionConflict | null;
  onRefetch: () => void;
  onMerge: () => void;
  onDiscard: () => void;
  onClose: () => void;
}

export function RecordPermissionConflictDialog({
  open,
  conflict,
  onRefetch,
  onMerge,
  onDiscard,
  onClose,
}: RecordPermissionConflictDialogProps): React.ReactElement | null {
  const { t } = useTranslation();

  if (!open || conflict === null) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="record-permission-conflict-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="record-permission-conflict-dialog"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2
          id="record-permission-conflict-title"
          className="text-sm font-semibold"
        >
          {t("admin.record_permissions.conflict.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("admin.record_permissions.conflict.body")}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Server version: {conflict.current_version}
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onClose}
            data-testid="record-permission-conflict-close"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onRefetch}
            data-testid="record-permission-conflict-refetch"
          >
            {t("admin.record_permissions.conflict.refetch")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onMerge}
            data-testid="record-permission-conflict-merge"
          >
            {t("admin.record_permissions.conflict.merge")}
          </button>
          <button
            type="button"
            className="rounded bg-destructive px-3 py-1 text-xs text-destructive-foreground"
            onClick={onDiscard}
            data-testid="record-permission-conflict-discard"
          >
            {t("admin.record_permissions.conflict.discard")}
          </button>
        </div>
      </div>
    </div>
  );
}
